<template>
  <div class="pagination-container">
    <div class="pagination-info">
      <span class="pagination-summary">
        {{ startItem }}-{{ endItem }} / {{ totalElements }}건
      </span>
      <div class="page-size-selector">
        <label for="pageSize">페이지 당</label>
        <select 
          id="pageSize" 
          :value="pageSize" 
          @change="handlePageSizeChange"
          class="page-size-select"
        >
          <option value="10">10개</option>
          <option value="20">20개</option>
          <option value="50">50개</option>
          <option value="100">100개</option>
        </select>
      </div>
    </div>

    <div class="pagination-controls">
      <button 
        @click="$emit('first-page')"
        :disabled="isFirstPage"
        class="pagination-btn"
        title="첫 페이지"
      >
        ≪
      </button>
      
      <button 
        @click="$emit('previous-page')"
        :disabled="!hasPrevious"
        class="pagination-btn"
        title="이전 페이지"
      >
        ‹
      </button>

      <div class="page-numbers">
        <button
          v-for="pageNum in visiblePages"
          :key="pageNum"
          @click="$emit('page-change', pageNum - 1)"
          :class="['page-number', { active: pageNum - 1 === currentPage }]"
        >
          {{ pageNum }}
        </button>
      </div>

      <button 
        @click="$emit('next-page')"
        :disabled="!hasNext"
        class="pagination-btn"
        title="다음 페이지"
      >
        ›
      </button>
      
      <button 
        @click="$emit('last-page')"
        :disabled="isLastPage"
        class="pagination-btn"
        title="마지막 페이지"
      >
        ≫
      </button>
    </div>
  </div>
</template>

<script setup>
import { computed } from 'vue'

const props = defineProps({
  currentPage: {
    type: Number,
    required: true
  },
  totalPages: {
    type: Number,
    required: true
  },
  totalElements: {
    type: Number,
    required: true
  },
  pageSize: {
    type: Number,
    required: true
  },
  hasNext: {
    type: Boolean,
    default: false
  },
  hasPrevious: {
    type: Boolean,
    default: false
  },
  maxVisiblePages: {
    type: Number,
    default: 5
  }
})

const emit = defineEmits([
  'page-change',
  'page-size-change',
  'first-page',
  'last-page',
  'previous-page',
  'next-page'
])

const isFirstPage = computed(() => props.currentPage === 0)
const isLastPage = computed(() => props.currentPage === props.totalPages - 1)

const startItem = computed(() => {
  if (props.totalElements === 0) return 0
  return props.currentPage * props.pageSize + 1
})

const endItem = computed(() => {
  const end = (props.currentPage + 1) * props.pageSize
  return Math.min(end, props.totalElements)
})

const visiblePages = computed(() => {
  const total = props.totalPages
  const current = props.currentPage + 1 // 1-based for display
  const max = props.maxVisiblePages
  
  if (total <= max) {
    return Array.from({ length: total }, (_, i) => i + 1)
  }
  
  let start = Math.max(1, current - Math.floor(max / 2))
  let end = Math.min(total, start + max - 1)
  
  if (end - start + 1 < max) {
    start = Math.max(1, end - max + 1)
  }
  
  return Array.from({ length: end - start + 1 }, (_, i) => start + i)
})

const handlePageSizeChange = (event) => {
  const newSize = parseInt(event.target.value)
  emit('page-size-change', newSize)
}
</script>

<style scoped>
.pagination-container {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px 0;
  border-top: 1px solid #e5e5e5;
}

.pagination-info {
  display: flex;
  align-items: center;
  gap: 20px;
}

.pagination-summary {
  font-size: 14px;
  color: #666;
}

.page-size-selector {
  display: flex;
  align-items: center;
  gap: 8px;
}

.page-size-selector label {
  font-size: 14px;
  color: #666;
}

.page-size-select {
  padding: 4px 8px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 14px;
}

.pagination-controls {
  display: flex;
  align-items: center;
  gap: 8px;
}

.pagination-btn {
  padding: 8px 12px;
  border: 1px solid #ddd;
  background: white;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
  color: #666;
  transition: all 0.2s ease;
}

.pagination-btn:hover:not(:disabled) {
  background: #f5f5f5;
  border-color: #999;
}

.pagination-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.page-numbers {
  display: flex;
  gap: 4px;
}

.page-number {
  padding: 8px 12px;
  border: 1px solid #ddd;
  background: white;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
  color: #666;
  transition: all 0.2s ease;
  min-width: 40px;
}

.page-number:hover {
  background: #f5f5f5;
  border-color: #999;
}

.page-number.active {
  background: #007bff;
  border-color: #007bff;
  color: white;
}

.page-number.active:hover {
  background: #0056b3;
  border-color: #0056b3;
}

@media (max-width: 768px) {
  .pagination-container {
    flex-direction: column;
    gap: 16px;
    align-items: stretch;
  }
  
  .pagination-info {
    justify-content: space-between;
  }
  
  .pagination-controls {
    justify-content: center;
  }
  
  .page-numbers {
    max-width: 200px;
    overflow-x: auto;
  }
}
</style>